import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Search, Star } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { api, fileUrl } from "@/lib/api";
import { Logo, LangSwitch, StatusBadge } from "@/components/common";
import { Field, Pick } from "@/app/tool/Options";
import SeoTab from "@/admin/SeoTab";
import PlansTab from "@/admin/PlansTab";

const useLoad = (url, params) => {
  const [data, setData] = useState(null);
  const key = JSON.stringify(params || {});
  const seq = useRef(0);
  const load = useCallback(() => {
    const id = ++seq.current;  // ignore responses from older requests
    return api.get(url, { params: JSON.parse(key) }).then((r) => { if (id === seq.current) setData(r.data); });
  }, [url, key]);
  useEffect(() => { load(); }, [load]);
  return [data, load];
};

function Stat({ label, value, testid }) {
  return <div className="panel p-5" data-testid={testid}><p className="text-xs text-neutral-400">{label}</p><p className="text-2xl font-semibold mt-1">{value}</p></div>;
}

function DataTable({ cols, rows, empty }) {
  return (
    <div className="panel overflow-x-auto">
      <Table>
        <TableHeader><TableRow>{cols.map((c) => <TableHead key={c}>{c}</TableHead>)}</TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 && <TableRow><TableCell colSpan={cols.length} className="text-neutral-500">{empty}</TableCell></TableRow>}
          {rows}
        </TableBody>
      </Table>
    </div>
  );
}

function Overview() {
  const { t, fmtMoney, fmtBytes, fmtNum } = useI18n();
  const [d] = useLoad("/admin/overview");
  if (!d) return null;
  const sum = (arr) => arr.map((x) => fmtMoney(x.total, x._id)).join(" · ") || fmtMoney(0);
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label={t("admin.users")} value={fmtNum(d.users)} testid="stat-users" />
        <Stat label={t("admin.activeSubs")} value={fmtNum(d.subscriptions.active)} testid="stat-active-subs" />
        <Stat label={t("admin.mrr")} value={sum(d.mrr)} testid="stat-mrr" />
        <Stat label={t("admin.revenue")} value={sum(d.revenue_this_month)} testid="stat-revenue" />
        <Stat label={t("admin.aiCost")} value={fmtMoney(d.ai_cost_usd_this_month || 0, "USD")} testid="stat-ai-cost" />
        <Stat label={t("admin.gens")} value={fmtNum(d.generations_this_month)} testid="stat-gens" />
        <Stat label={t("admin.storage")} value={fmtBytes(d.storage.reduce((a, b) => a + b.bytes, 0))} testid="stat-storage" />
        <Stat label={t("admin.jobsQ")} value={`${d.jobs.queued} / ${d.jobs.processing} / ${d.jobs.failed}`} testid="stat-jobs" />
      </div>
      <div className="panel p-5 text-sm grid sm:grid-cols-3 gap-3">
        {Object.entries(d.subscriptions).map(([k, v]) => <p key={k}><span className="text-neutral-400">{t(`sub.states.${k}`)}:</span> {v}</p>)}
      </div>
    </div>
  );
}

function DeleteUserDialog({ user, onDone, onError }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const run = async () => {
    try { await api.delete(`/admin/users/${user.id}`, { data: { confirm } }); setOpen(false); toast.success(t("admin.deletedOne")); onDone(); }
    catch (e) { onError(e); }
  };
  return (
    <AlertDialog open={open} onOpenChange={(o) => { setOpen(o); setConfirm(""); }}>
      <AlertDialogTrigger asChild><button className="text-red-400" data-testid={`admin-delete-${user.id}`}>{t("admin.delete")}</button></AlertDialogTrigger>
      <AlertDialogContent className="bg-[var(--bg-1)] border-[var(--line)]">
        <AlertDialogHeader>
          <AlertDialogTitle>{t("admin.deleteUser")}</AlertDialogTitle>
          <AlertDialogDescription>{t("admin.deleteText", { email: user.email })}</AlertDialogDescription>
        </AlertDialogHeader>
        <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={user.email} data-testid="admin-delete-confirm-input" />
        <AlertDialogFooter>
          <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
          <button disabled={confirm.trim().toLowerCase() !== user.email.toLowerCase()} onClick={run}
            className="rounded-md bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-semibold disabled:opacity-40" data-testid="admin-delete-confirm-btn">{t("admin.delete")}</button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function BulkDeleteDialog({ ids, onDone, onError }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const n = ids.length;
  const run = async () => {
    try {
      const { data } = await api.post("/admin/users/bulk-delete", { ids, confirm: Number(confirm) });
      setOpen(false);
      toast.success(t("admin.bulkResult", { d: data.deleted.length, s: data.skipped.length }));
      onDone(data);
    } catch (e) { onError(e); }
  };
  return (
    <AlertDialog open={open} onOpenChange={(o) => { setOpen(o); setConfirm(""); }}>
      <AlertDialogTrigger asChild>
        <button disabled={!n} className="rounded-lg bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-semibold disabled:opacity-40" data-testid="admin-bulk-delete-btn">
          {t("admin.deleteSelected")} ({n})
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[var(--bg-1)] border-[var(--line)]">
        <AlertDialogHeader>
          <AlertDialogTitle>{t("admin.deleteSelected")}</AlertDialogTitle>
          <AlertDialogDescription>{t("admin.bulkText", { n })}</AlertDialogDescription>
        </AlertDialogHeader>
        <Input inputMode="numeric" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={String(n)} data-testid="admin-bulk-confirm-input" />
        <AlertDialogFooter>
          <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
          <button disabled={confirm.trim() !== String(n)} onClick={run}
            className="rounded-md bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-semibold disabled:opacity-40" data-testid="admin-bulk-confirm-btn">{t("admin.delete")}</button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function Users() {
  const { t, fmtDate, errMsg } = useI18n();
  const { user: me } = useAuth();
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [d, load] = useLoad("/admin/users", { q: query, page });
  const [plans] = useLoad("/admin/plans");
  useEffect(() => { if (d && d.items.length === 0 && page > 1) setPage(Math.max(1, d.pages)); }, [d, page]);
  const [error, setError] = useState("");
  const [sel, setSel] = useState([]);
  const fail = (e) => { const m = errMsg(e); setError(m); toast.error(m); };
  const act = async (fn) => { setError(""); try { await fn(); load(); } catch (e) { fail(e); load(); } };
  const items = d?.items || [];
  const selectable = items.filter((u) => u.id !== me?.id).map((u) => u.id);
  const toggle = (id, on) => setSel((s) => (on ? [...s, id] : s.filter((x) => x !== id)));
  const afterBulk = (data) => {
    setSel([]);
    if (data.skipped.length) setError(data.skipped.map((x) => `${items.find((u) => u.id === x.id)?.email || x.id}: ${errMsg({ response: { data: { detail: x.code } } })}`).join(" · "));
    else setError("");
    load();
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <form onSubmit={(e) => { e.preventDefault(); setSel([]); setPage(1); setQuery(q); }} className="flex gap-2 w-full max-w-md">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("common.search")} data-testid="admin-user-search-input" />
          <button className="btn-brand rounded-lg px-3" data-testid="admin-user-search-btn"><Search size={16} /></button>
        </form>
        <BulkDeleteDialog ids={sel} onDone={afterBulk} onError={fail} />
      </div>
      {error && (
        <div role="alert" className="panel border-red-800 px-4 py-3 text-sm text-red-300 flex gap-3" data-testid="admin-users-error">
          <span className="flex-1">{error}</span>
          <button onClick={() => setError("")} className="text-neutral-400 text-xs">{t("common.close")}</button>
        </div>
      )}
      <DataTable empty={t("admin.noData")}
        cols={[
          <Checkbox key="all" aria-label={t("admin.selectAll")} checked={selectable.length > 0 && selectable.every((id) => sel.includes(id))}
            onCheckedChange={(on) => setSel(on ? selectable : [])} data-testid="admin-select-all" />,
          t("common.email"), t("admin.role"), t("admin.accountStatus"), t("sub.status"), t("aplans.plan"), t("admin.usage"), t("admin.trial"), t("billing.date"), ""]}
        rows={items.map((u) => {
          const self = u.id === me?.id;
          return (
            <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`} className={u.disabled ? "opacity-60" : ""}>
              <TableCell>
                <Checkbox disabled={self} checked={sel.includes(u.id)} onCheckedChange={(on) => toggle(u.id, on)} aria-label={u.email} data-testid={`admin-select-${u.id}`} />
              </TableCell>
              <TableCell>{u.email}{self && <span className="ml-2 text-[10px] text-emerald-400">({t("admin.you")})</span>}<span className="block text-xs text-neutral-500">{u.name}</span></TableCell>
              <TableCell className="min-w-[120px]">
                <Pick value={u.role} onChange={(v) => v !== u.role && act(() => api.patch(`/admin/users/${u.id}`, { role: v }))}
                  options={[["user", t("admin.roleUser")], ["admin", t("admin.roleAdmin")]]} testid={`admin-role-${u.id}`} />
              </TableCell>
              <TableCell>
                <label className="flex items-center gap-2 text-xs">
                  <Switch checked={!u.disabled} onCheckedChange={(on) => act(() => api.patch(`/admin/users/${u.id}`, { disabled: !on }))} data-testid={`admin-active-toggle-${u.id}`} />
                  <span data-testid={`admin-account-status-${u.id}`}>{u.disabled ? t("admin.disabled") : t("admin.enabled")}</span>
                </label>
              </TableCell>
              <TableCell>{t(`sub.states.${u.subscription_status || "none"}`)}</TableCell>
              <TableCell className="min-w-[130px]">{u.plan_id && plans ? (
                <Pick value={u.plan_id} onChange={(v) => v !== u.plan_id && act(() => api.post(`/admin/users/${u.id}/plan`, { plan_id: v }))}
                  options={plans.items.map((p) => [p.id, p.name])} testid={`admin-user-plan-${u.id}`} />
              ) : "—"}</TableCell>
              <TableCell>{u.quota ? `${u.used}/${u.quota}` : "—"}</TableCell>
              <TableCell>{u.trial_remaining}</TableCell>
              <TableCell>{fmtDate(u.created_at)}</TableCell>
              <TableCell className="whitespace-nowrap space-x-3 text-xs">
                <button className="text-emerald-400" onClick={() => act(() => api.post(`/admin/users/${u.id}/grant`, { months: 1 }))} data-testid={`admin-grant-${u.id}`}>{t("admin.grant")}</button>
                <button className="text-amber-400" onClick={() => act(() => api.post(`/admin/users/${u.id}/expire`))} data-testid={`admin-expire-${u.id}`}>{t("admin.expire")}</button>
                {!self && <DeleteUserDialog user={u} onDone={() => { setError(""); setSel((s) => s.filter((x) => x !== u.id)); load(); }} onError={fail} />}
              </TableCell>
            </TableRow>
          );
        })} />
      {d?.pages > 1 && (
        <div className="flex items-center justify-end gap-3 text-sm" data-testid="admin-users-pagination">
          <button disabled={page <= 1} onClick={() => { setSel([]); setPage(page - 1); }} className="rounded-lg border border-[var(--line)] px-3 py-1.5 disabled:opacity-40" data-testid="admin-users-prev">{t("common.prev")}</button>
          <span data-testid="admin-users-page">{t("common.page")} {page} {t("common.of")} {d.pages} · {d.total}</span>
          <button disabled={page >= d.pages} onClick={() => { setSel([]); setPage(page + 1); }} className="rounded-lg border border-[var(--line)] px-3 py-1.5 disabled:opacity-40" data-testid="admin-users-next">{t("common.next")}</button>
        </div>
      )}
    </div>
  );
}

function Subs() {
  const { t, fmtDate, fmtMoney } = useI18n();
  const [status, setStatus] = useState("all");
  const [d] = useLoad("/admin/subscriptions", status === "all" ? {} : { status });
  return (
    <div className="space-y-4">
      <div className="w-48"><Pick value={status} onChange={setStatus} options={[["all", t("common.all")], ...["active", "canceled", "unpaid", "expired", "pending", "failed"].map((k) => [k, t(`sub.states.${k}`)])]} testid="admin-sub-filter" /></div>
      <DataTable empty={t("admin.noData")} cols={[t("common.email"), t("sub.status"), t("billing.amount"), t("sub.period"), "Ref", t("billing.date")]}
        rows={(d?.items || []).map((s) => (
          <TableRow key={s.id}><TableCell>{s.email}</TableCell><TableCell>{t(`sub.states.${s.status}`)}{s.source === "admin_grant" && <span className="text-xs text-amber-300"> (grant)</span>}</TableCell>
            <TableCell>{fmtMoney(s.price, s.currency)}</TableCell><TableCell>{fmtDate(s.current_period_end)}</TableCell><TableCell className="font-mono text-xs">{s.provider_ref || "—"}</TableCell><TableCell>{fmtDate(s.created_at)}</TableCell></TableRow>
        ))} />
    </div>
  );
}

function Txns() {
  const { t, fmtDate, fmtMoney } = useI18n();
  const [d] = useLoad("/admin/transactions");
  return <DataTable empty={t("admin.noData")} cols={[t("billing.date"), t("common.email"), t("billing.type"), t("billing.amount"), t("billing.status"), t("billing.ref")]}
    rows={(d?.items || []).map((x) => (
      <TableRow key={x.id}><TableCell>{fmtDate(x.created_at, true)}</TableCell><TableCell>{x.email}</TableCell><TableCell>{t(`billing.types.${x.type}`)}</TableCell>
        <TableCell>{fmtMoney(x.amount, x.currency)}</TableCell><TableCell>{t(`billing.statuses.${x.status}`)}</TableCell><TableCell className="font-mono text-xs">{x.order_ref || x.provider_ref || "—"}</TableCell></TableRow>
    ))} />;
}

function Jobs() {
  const { t, fmtDate, fmtMoney } = useI18n();
  const errText = useErrText();
  const [status, setStatus] = useState("all");
  const [d] = useLoad("/admin/jobs", status === "all" ? {} : { status });
  return (
    <div className="space-y-4">
      <div className="w-48"><Pick value={status} onChange={setStatus} options={[["all", t("common.all")], ...["queued", "processing", "completed", "failed", "canceled"].map((k) => [k, t(`status.${k}`)])]} testid="admin-job-filter" /></div>
      <DataTable empty={t("admin.noData")} cols={["ID", t("admin.col.tool"), t("admin.col.provider"), t("billing.status"), t("admin.col.cost"), t("billing.date"), t("admin.col.error")]}
        rows={(d?.items || []).map((j) => (
          <TableRow key={j.id}><TableCell className="font-mono text-xs">{j.id}</TableCell><TableCell>{t(`tools.${j.tool}.name`)}</TableCell><TableCell>{j.provider}</TableCell>
            <TableCell><StatusBadge status={j.status} /></TableCell><TableCell>{fmtMoney(j.cost_usd || 0, "USD")}</TableCell><TableCell>{fmtDate(j.created_at, true)}</TableCell>
            <TableCell className="text-xs text-red-300 max-w-xs truncate" title={j.error}>{errText(j.error)}</TableCell></TableRow>
        ))} />
    </div>
  );
}

// Known error codes are shown translated; anything else (raw provider text) is shown as-is.
function useErrText() {
  const { t } = useI18n();
  return (x) => (x && Object.prototype.hasOwnProperty.call(t("errors"), x) ? t(`errors.${x}`) : x || "");
}

function Errors() {
  const { t, fmtDate } = useI18n();
  const errText = useErrText();
  const [d] = useLoad("/admin/errors");
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <DataTable empty={t("admin.noData")} cols={[t("billing.date"), t("admin.col.job"), t("admin.col.error")]} rows={(d?.items || []).map((e) => (
        <TableRow key={e.id}><TableCell>{fmtDate(e.created_at, true)}</TableCell><TableCell className="font-mono text-xs">{e.job_id}</TableCell><TableCell className="text-xs text-red-300" title={e.message}>{errText(e.message)}</TableCell></TableRow>))} />
      <DataTable empty={t("admin.noData")} cols={[t("billing.date"), t("admin.col.action"), t("admin.col.target")]} rows={(d?.audit || []).map((a) => (
        <TableRow key={a.id}><TableCell>{fmtDate(a.created_at, true)}</TableCell><TableCell className="text-xs" title={a.action}>{Object.prototype.hasOwnProperty.call(t("admin.actions"), a.action) ? t("admin.actions")[a.action] : a.action}</TableCell><TableCell className="font-mono text-xs">{a.target}</TableCell></TableRow>))} />
    </div>
  );
}

function SettingsTab({ cfg, setCfg }) {
  const { t, errMsg } = useI18n();
  const s = cfg.settings;
  const [f, setF] = useState({ currency: s.currency, trial_images: s.trial_images, tool_providers: s.tool_providers, cutout_engine: s.cutout_engine,
    bg_presets: JSON.stringify(s.bg_presets, null, 1), style_prompts: JSON.stringify(s.style_prompts, null, 1), sizes: JSON.stringify(s.sizes), provider_costs_usd: JSON.stringify(s.provider_costs_usd) });
  const [projects] = useLoad("/projects", { limit: 50 });
  const [show, setShow] = useState({});
  const save = async () => {
    try {
      const body = { ...f, trial_images: Number(f.trial_images),
        bg_presets: JSON.parse(f.bg_presets), style_prompts: JSON.parse(f.style_prompts), sizes: JSON.parse(f.sizes), provider_costs_usd: JSON.parse(f.provider_costs_usd) };
      setCfg((await api.put("/admin/settings", body)).data);
      toast.success(t("settings.saved"));
    } catch (e) { toast.error(e instanceof SyntaxError ? "JSON" : errMsg(e)); }
  };
  const provOpts = (edit) => Object.entries(cfg.providers).filter(([, p]) => !edit || p.edit).map(([k, p]) => [k, p.label]);
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="panel p-6 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("admin.currency")}><Pick value={f.currency} onChange={(v) => setF({ ...f, currency: v })} options={[["TRY", "TRY"], ["USD", "USD"], ["EUR", "EUR"]]} testid="admin-currency-select" /></Field>
          <Field label={t("admin.trialImages")}><Input type="number" value={f.trial_images} onChange={(e) => setF({ ...f, trial_images: e.target.value })} data-testid="admin-trial-input" /></Field>
        </div>
        <p className="text-sm font-medium pt-2">{t("admin.providers")}</p>
        {["hero", "model", "lifestyle", "ai_model"].map((k) => (
          <Field key={k} label={k === "ai_model" ? t("models.generateAi") : t(`tools.${k}.name`)}>
            <Pick value={f.tool_providers[k]} onChange={(v) => setF({ ...f, tool_providers: { ...f.tool_providers, [k]: v } })} options={provOpts(k !== "ai_model")} testid={`admin-provider-${k}`} />
          </Field>
        ))}
        <Field label={t("admin.cutout")}><Pick value={f.cutout_engine} onChange={(v) => setF({ ...f, cutout_engine: v })} options={[["birefnet", t("admin.cutoutBiref")], ["white_key", t("admin.cutoutWhite")]]} testid="admin-cutout-select" /></Field>
      </div>
      <div className="panel p-6 space-y-4">
        <Field label={t("admin.presets")}><Textarea rows={6} className="font-mono text-xs" value={f.bg_presets} onChange={(e) => setF({ ...f, bg_presets: e.target.value })} data-testid="admin-presets-input" /></Field>
        <Field label={t("admin.styles")}><Textarea rows={6} className="font-mono text-xs" value={f.style_prompts} onChange={(e) => setF({ ...f, style_prompts: e.target.value })} data-testid="admin-styles-input" /></Field>
        <Field label={t("admin.sizes")}><Textarea rows={3} className="font-mono text-xs" value={f.sizes} onChange={(e) => setF({ ...f, sizes: e.target.value })} data-testid="admin-sizes-input" /></Field>
        <Field label={t("admin.costs")}><Textarea rows={2} className="font-mono text-xs" value={f.provider_costs_usd} onChange={(e) => setF({ ...f, provider_costs_usd: e.target.value })} data-testid="admin-costs-input" /></Field>
        <button onClick={save} className="btn-brand rounded-full px-6 py-2.5 font-semibold" data-testid="admin-settings-save-btn">{t("common.save")}</button>
      </div>
      <div className="panel p-6 lg:col-span-2">
        <p className="text-sm font-medium mb-3">{t("landing.showcaseTitle")} — {t("admin.showcase")}</p>
        <div className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-8 gap-3">
          {(projects?.items || []).filter((p) => p.results?.length).map((p) => {
            const on = show[p.id] ?? !!p.showcase;
            return (
              <button key={p.id} onClick={async () => { const r = await api.post(`/admin/showcase/${p.id}`); setShow({ ...show, [p.id]: r.data.showcase }); }}
                className={`relative rounded-lg overflow-hidden border-2 ${on ? "border-amber-400" : "border-transparent"}`} data-testid={`admin-showcase-${p.id}`}>
                <img src={fileUrl(p.results[0].thumb_file_id)} alt="" className="aspect-square object-contain bg-white w-full" loading="lazy" />
                {on && <Star size={14} className="absolute top-1 right-1 text-amber-400 fill-amber-400" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Payments({ cfg, setCfg }) {
  const { t, errMsg } = useI18n();
  const iz = cfg.iyzico;
  const [f, setF] = useState({ mode: iz.mode, sandbox_api_key: "", sandbox_secret_key: "", live_api_key: "", live_secret_key: "", merchant_id: "" });
  const act = async (fn) => { try { setCfg((await fn()).data); toast.success(t("settings.saved")); } catch (e) { toast.error(errMsg(e)); } };
  const Badge = ({ ok }) => <span className={`text-xs px-2 py-0.5 rounded-full ${ok ? "bg-emerald-500/15 text-emerald-400" : "bg-neutral-500/15 text-neutral-400"}`}>{ok ? t("common.configured") : t("common.notConfigured")}</span>;
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="panel p-6 space-y-4" data-testid="admin-iyzico-form">
        <div className="flex flex-wrap gap-3 text-sm items-center">iyzico: <Badge ok={iz.configured} /> Merchant ID: <Badge ok={iz.merchant_id_set} /></div>
        <Field label={t("admin.iyzicoMode")}><Pick value={f.mode} onChange={(v) => setF({ ...f, mode: v })} options={[["sandbox", t("admin.sandbox")], ["live", t("admin.live")]]} testid="admin-iyzico-mode" /></Field>
        {["sandbox", "live"].map((m) => (
          <div key={m} className="grid grid-cols-2 gap-3">
            <Field label={`${t(`admin.${m}`)} — ${t("admin.apiKey")}`}><Input type="password" autoComplete="off" placeholder={iz[`${m}_set`] ? "••••••••" : ""} value={f[`${m}_api_key`]} onChange={(e) => setF({ ...f, [`${m}_api_key`]: e.target.value })} data-testid={`admin-${m}-api-key`} /></Field>
            <Field label={t("admin.secretKey")}><Input type="password" autoComplete="off" placeholder={iz[`${m}_set`] ? "••••••••" : ""} value={f[`${m}_secret_key`]} onChange={(e) => setF({ ...f, [`${m}_secret_key`]: e.target.value })} data-testid={`admin-${m}-secret-key`} /></Field>
          </div>
        ))}
        <Field label={t("admin.merchantId")}><Input type="password" autoComplete="off" placeholder={iz.merchant_id_set ? "••••••••" : ""} value={f.merchant_id} onChange={(e) => setF({ ...f, merchant_id: e.target.value })} data-testid="admin-merchant-id" /></Field>
        <p className="text-[11px] text-neutral-500">{t("admin.secretHint")}</p>
        <button onClick={() => act(async () => { const r = await api.put("/admin/iyzico", f); setF({ mode: f.mode, sandbox_api_key: "", sandbox_secret_key: "", live_api_key: "", live_secret_key: "", merchant_id: "" }); return r; })} className="btn-brand rounded-full px-6 py-2.5 font-semibold" data-testid="admin-iyzico-save-btn">{t("common.save")}</button>
      </div>
      <div className="space-y-6">
        <div className="panel p-6 text-sm space-y-3">
          <p><span className="text-neutral-400">{t("admin.callback")}:</span><code className="block font-mono text-xs mt-1 break-all" data-testid="admin-callback-url">{iz.callback_url}</code></p>
          <p><span className="text-neutral-400">{t("admin.webhook")}:</span><code className="block font-mono text-xs mt-1 break-all" data-testid="admin-webhook-url">{iz.webhook_url}</code></p>
          <ol className="list-decimal pl-5 space-y-1.5 text-neutral-300 pt-2">{t("admin.setup").map((x) => <li key={x}>{x}</li>)}</ol>
        </div>
        <div className="panel p-6 text-sm space-y-2" data-testid="admin-services">
          <p className="font-medium mb-1">{t("admin.services")}</p>
          <p className="flex justify-between">{t("admin.imageAi")} <Badge ok={cfg.services.image_ai} /></p>
          <p className="flex justify-between">{t("admin.objectStorage")} <Badge ok={cfg.services.object_storage} /></p>
          <p className="flex justify-between">{t("admin.googleAuth")} <Badge ok /></p>
          <p className="flex justify-between">{t("admin.email")} <Badge ok={cfg.services.email} /></p>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { t } = useI18n();
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setCfg(r.data)); }, []);
  const tabs = ["overview", "users", "subs", "txns", "jobs", "errors", "plans", "settings", "seo", "payments"];
  return (
    <div className="min-h-screen" data-testid="admin-page">
      <header className="border-b border-[var(--line)] bg-[var(--bg-1)]">
        <div className="max-w-[1400px] mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4"><Logo to="/admin" /><span className="text-xs text-amber-300 uppercase tracking-widest">{t("admin.title")}</span></div>
          <div className="flex items-center gap-4"><LangSwitch /><Link to="/app" className="text-sm text-neutral-400 hover:text-white flex items-center gap-1" data-testid="admin-back-to-app"><ArrowLeft size={15} />{t("nav.dashboard")}</Link></div>
        </div>
      </header>
      <main className="max-w-[1400px] mx-auto px-5 py-8">
        <Tabs defaultValue="overview">
          <TabsList className="flex-wrap h-auto mb-6">
            {tabs.map((k) => <TabsTrigger key={k} value={k} data-testid={`admin-tab-${k}`}>{t(`admin.tabs.${k}`)}</TabsTrigger>)}
          </TabsList>
          <TabsContent value="overview"><Overview /></TabsContent>
          <TabsContent value="users"><Users /></TabsContent>
          <TabsContent value="subs"><Subs /></TabsContent>
          <TabsContent value="txns"><Txns /></TabsContent>
          <TabsContent value="jobs"><Jobs /></TabsContent>
          <TabsContent value="errors"><Errors /></TabsContent>
          <TabsContent value="plans">{cfg && <PlansTab iyzicoReady={cfg.iyzico.configured} />}</TabsContent>
          <TabsContent value="settings">{cfg && <SettingsTab cfg={cfg} setCfg={setCfg} />}</TabsContent>
          <TabsContent value="seo"><SeoTab /></TabsContent>
          <TabsContent value="payments">{cfg && <Payments cfg={cfg} setCfg={setCfg} />}</TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
